/**
 * Klient nástroje Asym requesty.
 *
 * Volá `/api/v1/test-tools/asym/**` — obálku pro API nástroje, která uvnitř provolá
 * zákaznické `/api/v1/asym/**` a jeho výsledek zabalí do jednotné obálky se stabilním
 * kódem chyby, `canRetry` a `recoveryHint`. Zákaznická cesta vrací DTO napřímo, takže
 * by si obrazovka musela chyby skládat sama.
 *
 * Server zprávy jen sestaví a vrátí; nikam je neposílá.
 */

/** Diskriminátor obálky — server má jen dva stavy, nic se neřetězí. */
export type AsymToolStatus = "COMPLETED" | "FAILED";

/** Kam vydaná hodnota patří na drátě. */
export interface AsymNextCall {
  what?: string;
  method?: string;
  endpoint?: string;
  query?: Record<string, string>;
  payloadSchema?: Record<string, string>;
  hint?: string;
  /** Další volání, do kterých patří tatáž trojice hodnot. */
  alternatives?: AsymNextCall[];
}

export interface AsymToolError {
  code?: string;
  message?: string;
  canRetry?: boolean;
  recoveryHint?: string;
}

/** Společná obálka; vrací se i u 4xx, takže se tvar těla nemění podle stavového kódu. */
export interface AsymToolEnvelope<T = unknown> {
  status: AsymToolStatus;
  result?: T;
  nextCall?: AsymNextCall;
  error?: AsymToolError;
}

export interface AsymRequestResult {
  tokenId: string;
  operation: string;
  request: string;
}

export interface AsymSignatureResult {
  tokenId: string;
  signature: string;
  /** Base64 čtyřbajtového big-endian `scoringFlags` — posílá se spolu s podpisem. */
  additionalData: string;
  validPin: boolean;
}

export interface AsymApproveResult {
  tokenId: string;
  request: string;
  signature: string;
  additionalData: string;
  scoringFlags: number;
  validPin: boolean;
}

export interface AsymDecryptResult {
  tokenId: string;
  decrypted: string;
}

const BASE = "/api/v1/test-tools/asym";

/**
 * Nevyhazuje na non-2xx. Obálka má smysl i u 400/409/422 — nese `error.code`
 * i `recoveryHint`, tedy přesně to, co operátor potřebuje vidět. Vyhodit výjimku by ta
 * pole zahodilo a zbyl by holý stavový kód.
 */
async function post<T>(path: string, body: Record<string, unknown>): Promise<AsymToolEnvelope<T>> {
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });

  const text = await res.text();
  if (!text) {
    return { status: "FAILED", error: { message: `HTTP ${res.status}` } };
  }
  try {
    return JSON.parse(text) as AsymToolEnvelope<T>;
  } catch {
    // Tělo není JSON — typicky blok bezpečnostního filtru před controllerem.
    return { status: "FAILED", error: { message: text, code: `HTTP_${res.status}` } };
  }
}

/** Obálka pro danou operaci. */
export function buildAsymRequest(body: Record<string, unknown>) {
  return post<AsymRequestResult>("/request", body);
}

/**
 * Podpis zprávy od auth serveru. Bez tohohle kroku nejde schválit transakce ani
 * dokončit přihlášení — obálka requestu podpis neobsahuje.
 */
export function signAsymMessage(body: Record<string, unknown>) {
  return post<AsymSignatureResult>("/signature", body);
}

/** Obálka i podpis jedním voláním — protějšek SDK `createApproveTransactionRequest`. */
export function buildAsymApproveRequest(body: Record<string, unknown>) {
  return post<AsymApproveResult>("/approve-request", body);
}

/** Dešifruje zprávu, aby bylo vidět, co se podepisuje. */
export function decryptAsymMessage(body: Record<string, unknown>) {
  return post<AsymDecryptResult>("/decrypt", body);
}
